import { sectionRenderer } from '@theme/section-renderer';
import { RecentlyViewed } from '@theme/recently-viewed-products';

class RecentlyViewedProducts extends HTMLElement {
  #abortController = null;

  #initialized = false;

  connectedCallback() {
    if (this.#initialized) return;
    this.#initialized = true;
    this.#render();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
  }

  async #render() {
    const sectionId = this.dataset.sectionId;
    if (!sectionId) {
      this.#hideSection();
      return;
    }

    const maxDesktop = Number.parseInt(this.dataset.maxProductsDesktop || '', 10);
    const maxMobile = Number.parseInt(this.dataset.maxProductsMobile || '', 10);
    const maxProducts = Math.max(
      Number.isNaN(maxDesktop) ? 0 : maxDesktop,
      Number.isNaN(maxMobile) ? 0 : maxMobile,
      1
    );

    const productIds = this.#getProductIds(maxProducts);
    if (productIds.length === 0) {
      this.#renderEmptyState();
      return;
    }

    const searchUrl = Theme?.routes?.search_url || `${window.Shopify.routes.root}search`;
    const url = new URL(searchUrl, window.location.origin);
    url.searchParams.set('q', productIds.map((id) => `id:${id}`).join(' OR '));
    url.searchParams.set('type', 'product');
    url.searchParams.set('resources[type]', 'product');

    try {
      const controller = new AbortController();
      this.#abortController?.abort();
      this.#abortController = controller;

      const markup = await sectionRenderer.getSectionHTML(sectionId, false, url);
      if (controller.signal.aborted) return;

      const parsed = new DOMParser().parseFromString(markup, 'text/html');
      const incomingContent = parsed.querySelector('[data-recently-viewed-content]');
      const currentContent = this.querySelector('[data-recently-viewed-content]');

      if (!(incomingContent instanceof HTMLElement) || !(currentContent instanceof HTMLElement)) {
        this.#renderEmptyState();
        return;
      }

      currentContent.innerHTML = incomingContent.innerHTML;

      const hasItems = currentContent.querySelector('[data-product-id]');
      if (!hasItems) {
        this.#renderEmptyState();
        return;
      }

      this.#showSection();
      this.#setStatus('Recently viewed products loaded');
    } catch (error) {
      console.error('[recently-viewed] failed to render section', error);
      this.#renderEmptyState();
    }
  }

  #getProductIds(maxProducts) {
    const currentProductId = this.dataset.currentProductId ? String(this.dataset.currentProductId) : null;

    return RecentlyViewed.getProducts()
      .map((id) => String(id))
      .filter((id, index, allIds) => id && allIds.indexOf(id) === index && id !== currentProductId)
      .slice(0, maxProducts);
  }

  #setStatus(message) {
    const status = this.querySelector('[ref="status"]');
    if (status instanceof HTMLElement) {
      status.textContent = message;
    }
  }

  #hideSection() {
    const section = this.closest('[data-recently-viewed-section]');
    if (section instanceof HTMLElement) {
      section.hidden = true;
    }
  }

  #showSection() {
    const section = this.closest('[data-recently-viewed-section]');
    if (section instanceof HTMLElement) {
      section.hidden = false;
    }
  }

  #renderEmptyState() {
    const content = this.querySelector('[data-recently-viewed-content]');
    if (!(content instanceof HTMLElement)) {
      this.#hideSection();
      return;
    }

    const emptyMessage = (this.dataset.emptyMessage || '').trim();
    if (!emptyMessage) {
      this.#hideSection();
      return;
    }

    content.innerHTML = `<p class="recently-viewed__empty">${escapeHtml(emptyMessage)}</p>`;
    this.#showSection();
    this.#setStatus(emptyMessage);
  }
}

if (!customElements.get('recently-viewed-products')) {
  customElements.define('recently-viewed-products', RecentlyViewedProducts);
}

function escapeHtml(value) {
  const div = document.createElement('div');
  div.textContent = value;
  return div.innerHTML;
}
